import type { ReactNode } from 'react'

interface InsightCardProps {
  index?: number // For numbered badges
  title: string
  summary: string
  icon?: ReactNode
  iconColor?: string // Tailwind color class like 'text-green-600'
  borderColor?: string // Border hover color like 'border-green-300'
  bgColor?: string // Background hover color like 'bg-green-50'
  accentColor?: string // Accent color for text like 'text-green-600'
  onClick: () => void
  showBadge?: boolean // Show numbered badge
}

export default function InsightCard({
  index,
  title,
  summary,
  icon,
  iconColor = 'text-indigo-600',
  borderColor = 'border-indigo-300',
  bgColor = 'bg-indigo-50',
  accentColor = 'text-indigo-600',
  onClick,
  showBadge = false
}: InsightCardProps) {
  // Extract color name from Tailwind class (e.g., 'text-green-600' -> 'green')
  const getColorName = (colorClass: string) => {
    // Safety check for undefined/null values
    if (!colorClass) return 'indigo'
    const match = colorClass.match(/-(green|blue|orange|purple|indigo|amber|red|yellow)-/)
    return match ? match[1] : 'indigo'
  }

  const colorName = getColorName(accentColor) || 'indigo'
  const badgeBgClass = `bg-${colorName}-100`
  const badgeTextClass = `text-${colorName}-700`

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 rounded-lg border border-gray-200 hover:${borderColor} hover:${bgColor} transition-all group`}
    >
      <div className="flex items-start gap-2">
        {showBadge && typeof index !== 'undefined' && (
          <span
            className={`flex-shrink-0 w-5 h-5 rounded-full ${badgeBgClass} ${badgeTextClass} flex items-center justify-center text-xs font-semibold mt-0.5`}
          >
            {index + 1}
          </span>
        )}
        {icon && (
          <span className={`flex-shrink-0 ${iconColor} mt-0.5`}>
            {icon}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 text-sm mb-1">{title}</div>
          <p className="text-xs text-gray-600 line-clamp-2">{summary}</p>
          <span className={`text-xs ${accentColor} font-medium mt-1 inline-block group-hover:underline`}>
            View details →
          </span>
        </div>
      </div>
    </button>
  )
}
